import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@aqarmap/aqarmap-ui';
import { useAppDispatch } from '~/redux/hooks';
import useBreakpoint from '~/hooks/useBreakpoint';
import {
  openModal,
  setFooterDisplay,
} from '~/redux/features/uiState/uiStateSlice';
import useQuery from '~/hooks/useQuery';
import { useFullRoutes, useWallet } from '~/hooks';
import { formatNumber } from '~/lib';
import { useGetPackageDetailsQuery } from '~/redux/api/packagesApiSlice';
import { usePostPaymentPayMutation } from '~/redux/api/PaymentApiSlice';
import { Trans } from 'react-i18next';
import PackageSummary from '~/pages/Packages/Checkout/PackageSummary';
import { SelectedPaymentType } from '~/types/paymentTypes';
import { UserCardFormHandle } from '~/components/UserCardForm/types';
import PurchasePaymentMethod from '~/components/PurchasePaymentMethod';
import PaymentSummaryWrapper from '~/components/PaymentSummaryWrapper';
import '~/cardsIcons.css';
import { RequestStatusEnum } from '~/constants/enums';
import {
  PaymentMethodEnum,
  PaymentOperationTypeEnum,
} from '~/constants/enums/paymentEnums';
import { sendGTMEvent } from '~/utils';
import { CreditPackageType } from '~/types/packagesType';
import { getPriceWithTaxes } from '~/pages/Packages/utils';

export default function Checkout() {
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const { status } = useQuery();
  const { PACKAGES_URL, MY_ACCOUNT_URL } = useFullRoutes();
  const [checkoutError, setCheckoutError] = useState<string>('');
  const { mobileScreen } = useBreakpoint();
  const { t, i18n } = useTranslation('app');
  const locale = i18n.language;
  const userCardFormRef = useRef<UserCardFormHandle>(null);

  const [selectedPayment, setSelectedPayment] = useState<
    SelectedPaymentType | undefined
  >(undefined);
  const [isPayfortLoading, setIsPayfortLoading] = useState<boolean>(false);
  const { walletBalance } = useWallet({});
  const [postPaymentPay, { isLoading: isSubscribeLoading }] =
    usePostPaymentPayMutation();

  const {
    data: packageDetails,
    isLoading: isPackageLoading,
    isSuccess: isPackageSuccess,
  } = useGetPackageDetailsQuery({ id, locale });
  const price = getPriceWithTaxes(
    packageDetails?.price_after_discount ?? 0,
    packageDetails?.taxes_percentage,
  );
  const payWithWallet = !!selectedPayment?.payWithWallet;
  const payWithCash = !!selectedPayment?.payWithCash;
  const hasSelectedPayment =
    payWithWallet || !!selectedPayment?.isNew || Boolean(selectedPayment?.id);
  const hasEnoughBalance = walletBalance >= price;
  const isWalletBalanceInsufficient = payWithWallet && !hasEnoughBalance;
  const isSubmitDisabled =
    !hasSelectedPayment ||
    payWithCash ||
    isSubscribeLoading ||
    isPayfortLoading ||
    isWalletBalanceInsufficient;
  const priceWithTax = getPriceWithTaxes(
    packageDetails?.price_after_discount ?? 0,
    packageDetails?.taxes_percentage ?? 0,
  );
  const fees = priceWithTax + Number(packageDetails?.activation_fee || 0);

  const handlePayWithSavedCard = async () => {
    const response = await postPaymentPay({
      paymentMethod: PaymentMethodEnum.SAVED_CARD,
      operationType: PaymentOperationTypeEnum.PACKAGE,
      cardId: selectedPayment?.id,
      packageId: packageDetails?.id,
    });
    const { data, error } = response;
    if (data?.status === 'success') {
      window.location.href = MY_ACCOUNT_URL.USER_SUBSCRIPTION;
    } else if (data?.status === '3ds_required' && data?.['3ds_url']) {
      window.location.href = data['3ds_url'];
    } else {
      if (error && 'data' in error && (error.data as any)?.message) {
        setCheckoutError((error.data as any).message);
      } else {
        setCheckoutError(t('somethingWrong'));
      }
    }
  };

  const handlePayWithWallet = async () => {
    if (isWalletBalanceInsufficient) return;
    const response = await postPaymentPay({
      paymentMethod: PaymentMethodEnum.WALLET,
      operationType: PaymentOperationTypeEnum.PACKAGE,
      packageId: packageDetails?.id,
    });
    const { data, error } = response;
    if (data?.status === 'success') {
      window.location.href = MY_ACCOUNT_URL.USER_SUBSCRIPTION;
      return;
    }
    if (error && 'data' in error && (error.data as any)?.message) {
      setCheckoutError((error.data as any).message);
    } else {
      setCheckoutError(t('somethingWrong'));
    }
  };

  const handlePayWithCash = async () => {
    const response = await postPaymentPay({
      paymentMethod: PaymentMethodEnum.CASH,
      operationType: PaymentOperationTypeEnum.PACKAGE,
      packageId: packageDetails?.id,
    });
    const { data, error } = response;
    if (data?.status === 'success') {
      window.location.href = `${process.env.REACT_APP_BASE_URL}/${locale}/user/credit`;
    } else if (error && 'data' in error && (error.data as any)?.message) {
      setCheckoutError((error.data as any).message);
    } else {
      setCheckoutError(t('somethingWrong'));
    }
  };

  const onCheckoutSubmit = () => {
    const isNewCard = selectedPayment?.isNew;
    const isValidPlan = id && Boolean(packageDetails?.id);
    const isValidUserCardForm = userCardFormRef.current;
    if (isValidPlan) {
      sendGTMEvent({ event: 'gtm-package_pay_now_clicked' });
      if (payWithWallet) {
        handlePayWithWallet();
      } else if (payWithCash) {
        handlePayWithCash();
      } else if (isNewCard && isValidUserCardForm) {
        setIsPayfortLoading(true);
        userCardFormRef.current.submitUserCard({
          operationType: PaymentOperationTypeEnum.PACKAGE,
          packageId: Number(packageDetails?.id),
          returnTo: `${process.env.REACT_APP_BASE_URL}${PACKAGES_URL.CHECKOUT(id)}`,
        });
      } else {
        handlePayWithSavedCard();
      }
    }
  };

  const handleStatusFallback = () => {
    switch (status) {
      case RequestStatusEnum.FAILED:
        sendGTMEvent({
          event: 'gtm-package_payment',
          package_payment_status: 'failed',
        });
        setCheckoutError(t('somethingWrong'));
        setSelectedPayment({
          isNew: true,
          id: 0,
          payWithWallet: false,
        });
        break;
      case RequestStatusEnum.SUCCESS:
        sendGTMEvent({
          event: 'gtm-package_payment',
          package_payment_status: 'success',
        });
        window.location.href = `${process.env.REACT_APP_BASE_URL}/${locale}/user/credit`;
        break;
    }
  };

  useEffect(() => {
    if (mobileScreen) {
      dispatch(setFooterDisplay('hide'));
    }
    return () => {
      dispatch(setFooterDisplay('show'));
    };
  }, [mobileScreen]);

  useEffect(() => {
    handleStatusFallback();
  }, [status]);

  return (
    <div className="layout-boundary pt-4x pb-[40px] md:pb-[80px]">
      <div className="layout-boundary__inner flex flex-col-reverse md:flex-row gap-4x justify-between px-2x md:px-0">
        <PurchasePaymentMethod
          title={t('packagePaymentInfo')}
          enableWallet={true}
          enableCash={false}
          setSelectedPayment={setSelectedPayment}
          selectedPayment={selectedPayment}
          error={checkoutError}
          newCardProps={{
            onFailure: () => setCheckoutError(t('somethingWrong')),
            ref: userCardFormRef,
          }}
        />
        <PaymentSummaryWrapper
          isLoading={isPackageLoading || !isPackageSuccess}
          totalFeesLabel={t('totalFees')}
          enableTermsAndConditions
          submitBtnProps={{
            onClick: onCheckoutSubmit,
            isLoading: isSubscribeLoading || isPayfortLoading,
            disabled: isSubmitDisabled,
          }}
          fees={`${formatNumber(fees)} ${t('egp')}`}
          summary={
            <PackageSummary
              packageDetails={packageDetails as CreditPackageType}
            />
          }
          title={t('packagePaymentSummary')}
        />
        <div className="fixed bottom-0 left-0 z-[40] flex w-full flex-col items-center gap-1.5x border-t border-gray bg-white px-2x pt-2x pb-3x md:hidden">
          <Button
            type="button"
            size="medium"
            variant="primary"
            className="w-full md:hidden"
            onClick={onCheckoutSubmit}
            isLoading={isSubscribeLoading || isPayfortLoading}
            disabled={isSubmitDisabled}
          >
            {t('pay')}
          </Button>
          <p className="text-center text-caption-1 text-gray__dark_1">
            <Trans
              i18nKey="termsAndConditions"
              t={t}
              components={{
                tag: (
                  <button
                    className="text-title-5 text-blue hover:underline"
                    onClick={() => {
                      dispatch(openModal('termsAndConditionsModal'));
                    }}
                  />
                ),
              }}
            />
          </p>
        </div>
      </div>
    </div>
  );
}
